/* eslint-disable @typescript-eslint/no-explicit-any */
"use server";

import { sdk } from "@/lib/medusa";
import medusaError from "@/src/lib/util/medusa-error";
import { HttpTypes } from "@medusajs/types";
import { revalidateTag } from "next/cache";
import { redirect } from "next/navigation";
import {
  getAuthHeaders,
  getCacheOptions,
  getCacheTag,
  getCartId,
  removeCartId,
  setCartId,
} from "../../../lib/data/cookies";
import { getRegion } from "../../../lib/data/regions";
import { updateTag } from "next/cache";
import { ORDER_CACHE_TAG } from "@/modules/order/actions/order";
import {
  bounceOnExpiredSession,
  withSession,
} from "@/lib/data/session";
import { LOCATION_DISABLED } from "@/lib/constants";
import logger from "@/lib/util/logger";

/**
 * Retrieves a cart by its ID. If no ID is provided, it will use the cart ID from the cookies...
 * @param cartId - optional - The ID of the cart to retrieve.
 * @returns The cart object if found, or null if not found.
 */
export async function retrieveCart({
  currency_code,
  cartId,
}: {
  cartId?: string;
  currency_code: string;
}) {
  const id = cartId || (await getCartId());

  if (!id) {
    return null;
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  const next = {
    ...(await getCacheOptions("carts")),
  };

  const fetchCart = () =>
    sdk.client
      .fetch<HttpTypes.StoreCartResponse>(`/store/carts/get/${id}`, {
        method: "GET",
        query: {
          currency_code,
        },
        headers,
        next,
        cache: "no-store", // ensuring fresh data
      })
      .then(({ cart }) => cart)
      .catch(() => null);

  let cart = await fetchCart();

  logger.info("Retrieved cart from medusa store api", { cart, currency_code });
  if (cart && cart.currency_code !== currency_code.toLowerCase()) {
    try {
      await sdk.client.fetch<HttpTypes.StoreCartResponse>(
        `/store/carts/${id}/currency`,
        {
          method: "PUT",
          body: { currency_code: currency_code.toLowerCase() },
          headers,
          next,
          cache: "no-store", // ensuring fresh data
        }
      );
      // Re-read instead of trusting the PUT body: that route returns the
      // updateCartWorkflow result, which is typed `undefined`, so it responds
      // `{}`. Assigning it blanked the cart, which empties the checkout and
      // remounts the payment step. The GET is also the only path that builds
      // the full totals shape.
      cart = (await fetchCart()) ?? cart;

      const cartCacheTag = await getCacheTag("carts");
      revalidateTag(cartCacheTag, "max");

      // Invalidate shipping options cache since currency changed
      const shippingCacheTag = await getCacheTag("shippingOptions");
      if (shippingCacheTag) {
        revalidateTag(shippingCacheTag, "max");
      }
    } catch (e) {
      logger.error("Failed to update cart currency code", e);
    }
  }

  return cart;
}

export async function getOrSetCart(
  countryCode?: string,
  currency_code?: string,
  regionId?: string
) {
  // Get region either from regionId directly or from countryCode
  let effectiveRegionId = regionId;

  if (!effectiveRegionId && countryCode) {
    const region = await getRegion(countryCode);
    if (!region) {
      throw new Error(`Region not found for country code: ${countryCode}`);
    }
    effectiveRegionId = region.id;
  }

  if (!effectiveRegionId) {
    throw new Error(`Either regionId or countryCode must be provided`);
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  // Fetch cart
  let cart = await retrieveCart({ currency_code: currency_code || "eur" });

  logger.info("getOrSetCart - retrieved cart", { cart, regionId, effectiveRegionId,  });

  if (!cart) {
    logger.info('CREATE CART', effectiveRegionId, currency_code)
    const cartResp = await sdk.store.cart.create(
      {
        region_id: effectiveRegionId,
        currency_code: currency_code || "eur",
      },
      {},
      headers
    );
    
    logger.info('CART CREATED')
    cart = cartResp.cart;

    await setCartId(cart.id);

    const cartCacheTag = await getCacheTag("carts");
    revalidateTag(cartCacheTag, "max");
  }

  // if (cart && cart?.region_id !== effectiveRegionId) {
  //   await sdk.store.cart.update(
  //     cart.id,
  //     //@ts-expect-error - region_id is required by the API but not in the type definition
  //     { region_id: effectiveRegionId, currency_code: currency_code || "usd" },
  //     {},
  //     headers
  //   );
  //   const cartCacheTag = await getCacheTag("carts");
  //   revalidateTag(cartCacheTag, "max");
  // }

  return cart;
}

export async function updateCart(data: HttpTypes.StoreUpdateCart) {
  const cartId = await getCartId();
logger.info("Updating cart with data", { cartId, data });
  if (!cartId) {
    throw new Error(
      "No existing cart found, please create one before updating"
    );
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  return withSession(() =>
    sdk.store.cart
      .update(cartId, data, {}, headers)
      .then(async ({ cart }) => {
      const cartCacheTag = await getCacheTag("carts");
      updateTag(cartCacheTag);

      const fulfillmentCacheTag = await getCacheTag("fulfillment");
      updateTag(fulfillmentCacheTag);

        return cart;
      })
      .catch(medusaError)
  );
}

export async function addToCart({
  variantId,
  quantity,
  countryCode,
  regionId,
  currency_code,
  province,
  municipality,
}: {
  variantId: string;
  quantity: number;
  countryCode?: string;
  regionId?: string;
  currency_code: string;
  province?: string;
  municipality?: string;
}) {
  if (!variantId) {
    throw new Error("Missing variant ID when adding to cart");
  }

  const cart = await getOrSetCart(countryCode, currency_code, regionId);

logger.info("CART", cart)

  if (!cart) {
    throw new Error("Error retrieving or creating cart");
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  // Validate location-aware stock before adding to cart
  try {
    await sdk.client.fetch(`/store/carts/${cart.id}/validate-stock`, {
      method: "POST",
      headers,
      body: {
        variant_id: variantId,
        quantity,
        country_code: countryCode || "cu",
        province,
        municipality,
      },
      cache: "no-store",
    });
  } catch (error: any) {
    await bounceOnExpiredSession(error);
    throw (
      error?.message ||
      "No hay suficiente stock disponible en tu ubicación para esta cantidad."
    );
  }

  return await sdk.store.cart
    .createLineItem(
      cart.id,
      {
        variant_id: variantId,
        quantity,
      },
      {},
      headers
    )
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts");
      updateTag(cartCacheTag);

      // console.debug('2.', cartCacheTag)

      const fulfillmentCacheTag = await getCacheTag("fulfillment");

      // console.debug('3.', fulfillmentCacheTag)
      updateTag(fulfillmentCacheTag);
    })
    .catch((medusaError) => {
      // console.debug('0.0', JSON.stringify(medusaError, null, 4))

      // TODO Temporarily locally managed message until added localization to backend.
      if (medusaError.status === 400)
        throw "Error al editar carrito. No existe suficiente cantidad de producto en disponibilidad.";
      else throw "Error al editar el carrito.";
    });
}

export async function updateLineItem({
  lineId,
  quantity,
  countryCode,
  province,
  municipality,
}: {
  lineId: string;
  quantity: number;
  countryCode?: string;
  province?: string;
  municipality?: string;
}) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when updating line item");
  }

  const cartId = await getCartId();

  if (!cartId) {
    throw new Error("Missing cart ID when updating line item");
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  // Retrieve cart to find the variant being updated
  let cart: HttpTypes.StoreCart | null = null;
  try {
    const resp = await sdk.client.fetch<HttpTypes.StoreCartResponse>(
      `/store/carts/get/${cartId}`,
      {
        method: "GET",
        headers,
        cache: "no-store",
      }
    );
    cart = resp.cart;
  } catch {
    return { error: "Error al recuperar el carrito." };
  }

  const item = cart?.items?.find((i) => i.id === lineId);
  const variantId = item?.variant_id || item?.variant?.id;

  if (!variantId) {
    return { error: "No se encontró la variante del producto." };
  }

  // Validate location-aware stock before updating quantity
  try {
    await sdk.client.fetch(`/store/carts/${cartId}/validate-stock`, {
      method: "POST",
      headers,
      body: {
        variant_id: variantId,
        quantity,
        country_code: countryCode || "cu",
        province,
        municipality,
      },
      cache: "no-store",
    });
  } catch (error: any) {
    await bounceOnExpiredSession(error);
    return {
      error:
        error?.message ||
        "No hay suficiente stock disponible en tu ubicación para esta cantidad.",
    };
  }

  return await sdk.store.cart
    .updateLineItem(cartId, lineId, { quantity }, {}, headers)
    .then(async () => {
      const cartCacheTag = await getCacheTag("carts");
      updateTag(cartCacheTag);

      const fulfillmentCacheTag = await getCacheTag("fulfillment");
      updateTag(fulfillmentCacheTag);

      return { success: true };
    })
    .catch((medusaError) => {
      // TODO Temporarily locally managed message until added localization to backend.
      if (medusaError.status === 400)
        return {
          error:
            "Error al editar carrito. No existe suficiente cantidad de producto en disponibilidad.",
        };
      else return { error: "Error al editar el carrito." };
    });
}

export async function deleteLineItem(lineId: string) {
  if (!lineId) {
    throw new Error("Missing lineItem ID when deleting line item");
  }

  const cartId = await getCartId();

  if (!cartId) {
    throw new Error("Missing cart ID when deleting line item");
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  await withSession(() =>
    sdk.store.cart
      .deleteLineItem(cartId, lineId, {}, headers)
      .then(async () => {
        const cartCacheTag = await getCacheTag("carts");
        updateTag(cartCacheTag);

        const fulfillmentCacheTag = await getCacheTag("fulfillment");
        updateTag(fulfillmentCacheTag);
      })
      .catch(medusaError)
  );
}

export async function setShippingMethod({
  cartId,
  shippingMethodId,
}: {
  cartId: string;
  shippingMethodId: string;
}) {
  const headers = {
    ...(await getAuthHeaders()),
  };

  return withSession(() =>
    sdk.store.cart
      .addShippingMethod(cartId, { option_id: shippingMethodId }, {}, headers)
      .then(async () => {
        const cartCacheTag = await getCacheTag("carts");
        updateTag(cartCacheTag);
      })
      .catch(medusaError)
  );
}

export async function setBulkShippingMethods({
  cartId,
  optionIds,
  assignments,
}: {
  cartId: string;
  optionIds: string[];
  assignments?: Record<string, string[]>;
}) {
  const headers = {
    ...(await getAuthHeaders()),
  };

  return withSession(() =>
    sdk.client.fetch(`/store/carts/${cartId}/shipping-methods/bulk`, {
      method: "POST",
      headers,
      body: { options: optionIds, assignments },
      cache: "no-store",
    })
  );
}

export async function initiatePaymentSession(
  cart: HttpTypes.StoreCart,
  data: HttpTypes.StoreInitializePaymentSession
) {
  const headers = {
    ...(await getAuthHeaders()),
  };

  return withSession(() =>
    sdk.store.payment
      .initiatePaymentSession(cart, data, {}, headers)
      .then(async (resp) => {
      const cartCacheTag = await getCacheTag("carts");
      // NOT updateTag: this action is called from a mount effect, and updateTag
      // sets x-action-revalidated, which refreshes the route -> remounts the
      // payment step -> initiates again. revalidateTag (stale-while-revalidate)
      // invalidates without forcing the client refresh, matching the other
      // non-interactive cart actions here.
        revalidateTag(cartCacheTag, "max");
        return resp;
      })
      .catch(medusaError)
  );
}

export async function listPaymentProviders(regionId: string) {
  const resp = await sdk.store.payment.listPaymentProviders({
    region_id: regionId,
  });
  return resp.payment_providers;
}

export async function getPayPalClientToken() {
  const resp = await sdk.client.fetch<{ client_token: string }>(
    "/store/paypal/client-token",
    { method: "POST" }
  );
  return resp.client_token;
}

export async function applyPromotions(codes: string[]) {
  const cartId = await getCartId();

  if (!cartId) {
    throw new Error("No existing cart found");
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  return withSession(() =>
    sdk.store.cart
      .update(cartId, { promo_codes: codes }, {}, headers)
      .then(async () => {
        const cartCacheTag = await getCacheTag("carts");
        revalidateTag(cartCacheTag, "max");

        const fulfillmentCacheTag = await getCacheTag("fulfillment");
        revalidateTag(fulfillmentCacheTag, "max");
      })
      .catch(medusaError)
  );
}

export async function submitPromotionForm(
  currentState: any,
  formData: FormData
) {
  const code = formData.get("code") as string;
  try {
    await applyPromotions([code]);
  } catch (e: any) {
    // applyPromotions bounces on 401; without this the redirect it throws
    // would be swallowed and returned as a form error string.
    await bounceOnExpiredSession(e);
    return e.message;
  }
}

export async function setAddresses(currentState: any, formData: FormData) {
  try {
    if (!formData) {
      throw new Error("No form data found when setting addresses");
    }
    const cartId = await getCartId();
    if (!cartId) {
      throw new Error("No existing cart found when setting addresses");
    }

    const data = {
      shipping_address: {
        first_name: formData.get("shipping_address.first_name"),
        last_name: formData.get("shipping_address.last_name"),
        address_1: formData.get("shipping_address.address_1"),
        address_2: "",
        company: formData.get("shipping_address.company"),
        postal_code: formData.get("shipping_address.postal_code"),
        city: formData.get("shipping_address.city"),
        country_code: formData.get("shipping_address.country_code"),
        province: formData.get("shipping_address.province"),
        phone: formData.get("shipping_address.phone"),
      },
      email: formData.get("email"),
    } as any;

    const sameAsBilling = formData.get("same_as_billing");
    if (sameAsBilling === "on") data.billing_address = data.shipping_address;

    if (sameAsBilling !== "on") {
      data.billing_address = {
        first_name: formData.get("billing_address.first_name"),
        last_name: formData.get("billing_address.last_name"),
        address_1: formData.get("billing_address.address_1"),
        address_2: "",
        company: formData.get("billing_address.company"),
        postal_code: formData.get("billing_address.postal_code"),
        city: formData.get("billing_address.city"),
        country_code: formData.get("billing_address.country_code"),
        province: formData.get("billing_address.province"),
        phone: formData.get("billing_address.phone"),
      };
    }

    await updateCart(data);

    return {
      success: true,
      countryCode: formData.get("shipping_address.country_code"),
      error: null,
    };
  } catch (e: any) {
    await bounceOnExpiredSession(e);
    console.error("Error setting addresses:", e);
    return {
      success: false,
      countryCode: null,
      error: e.message,
    };
  }
}

export async function placeOrder(cartId?: string) {
  const id = cartId || (await getCartId());

  if (!id) {
    throw new Error("No existing cart found when placing an order");
  }

  const headers = {
    ...(await getAuthHeaders()),
  };

  let cartRes: any;
  try {
    cartRes = await sdk.store.cart.complete(id, {}, headers);
    const cartCacheTag = await getCacheTag("carts");
    revalidateTag(cartCacheTag, "max");
  } catch (error: any) {
    await bounceOnExpiredSession(error);

    // Location disabled, from our custom complete route. RETURN it, don't
    // throw: Next redacts errors thrown from server actions in production, so
    // the JSON-in-Error.message this used to rely on reached the client as an
    // unparseable paragraph and blew up the caller's JSON.parse. Return values
    // are never redacted.
    if (error?.status === 409) {
      await removeCartId();
      return {
        type: LOCATION_DISABLED,
        message: error?.message || "Location is disabled",
      } as const;
    }
    medusaError(error);
  }

  if (cartRes?.type === "order") {
    updateTag(await getCacheTag(await ORDER_CACHE_TAG()));

    await removeCartId();
    return cartRes.order;
  }

  throw new Error(cartRes?.error?.message || "Failed to place order");
}

export async function updateRegion(countryCode: string, currentPath: string) {
  const cartId = await getCartId();
  const region = await getRegion(countryCode);

  if (!region) {
    throw new Error(`Region not found for country code: ${countryCode}`);
  }

  if (cartId) {
    await clearCart();
  }

  const cartCacheTag = await getCacheTag("carts");
  revalidateTag(cartCacheTag, "max");

  const regionCacheTag = await getCacheTag("regions");
  revalidateTag(regionCacheTag, "max");

  const productsCacheTag = await getCacheTag("products");
  revalidateTag(productsCacheTag, "max");

  redirect(`/${countryCode}${currentPath}`);
}

export interface ShippingOptionGroup {
  stock_location_id: string;
  stock_location_name: string;
  items: HttpTypes.StoreCartLineItem[];
  shipping_options: HttpTypes.StoreCartShippingOption[];
}

export async function listCartOptions(
  cartId?: string,
  regionId?: string,
  province?: string,
  countryCode?: string,
  municipality?: string
) {
  const finalCartId = cartId || (await getCartId());
  const headers = {
    ...(await getAuthHeaders()),
  };
  const next = {
    ...(await getCacheOptions("shippingOptions")),
  };

  const query: Record<string, string> = {
    cart_id: finalCartId!,
  };

  if (regionId) {
    query.region_id = regionId;
  }

  if (province) {
    query.province = province;
  }

  if (countryCode) {
    query.country_code = countryCode;
  }

  if (municipality) {
    query.municipality = municipality;
  }

  const resp = await withSession(() =>
    sdk.client.fetch<{
      groups: ShippingOptionGroup[];
    }>("/store/store-shipping-options", {
      query,
      next,
      headers,
      cache: "no-store",
    })
  );

  return resp.groups || [];
}

export async function getCartItemCount(currency_code: string) {
  const cart = await retrieveCart({ currency_code });
  if (!cart?.items?.length) return 0;
  return cart.items.reduce((total, item) => total + item.quantity, 0);
}

export async function clearCart() {
  return await removeCartId();
}
